import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import { ProductCardViewModel } from '../../../Partials/ProductCard/ProductCardViewModel.csharp';
import { ProductList } from '../../../Partials/ProductList/ProductList';
import { ProductListVariant } from '../../../Partials/ProductList/ProductListVariant.csharp';
import { ProductListViewModel } from '../../../Partials/ProductList/ProductListViewModel.csharp';
import { postFor } from '../../../ReactComponents/API';
import { ApplicationInsights, SeverityLevel } from '../../../ReactComponents/ApplicationInsights';
import { Recommendation } from '../../ViewModels/Recommendation.csharp';
import { RecommendationsCache } from '../../ViewModels/RecommendationsCache';
import { RecommendationsEventNameResolver } from '../../ViewModels/RecommendationsEventNameResolver';
import { RecommendedProductCardsRequest } from '../../ViewModels/RecommendedProductCardsRequest.csharp';
import { RecommendedProductsProps } from '../../ViewModels/RecommendedProductsProps.csharp';

interface RecommendedProductsInterface extends RecommendedProductsProps {
    disableProductQuantityPicker?: boolean;
}

export const RecommendedProducts = (props: RecommendedProductsInterface) => {
    const [productList, setProductList] = useState<ProductCardViewModel[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [productListModel, setProductListModel] = useState<ProductListViewModel>({
        products: [],
        ...props.model,
        powerReviewsProductListViewModel: {}
    });

    const RecommendationsTimeoutMs = 2000;
    const recommendationsLoadTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const variantMobile = props.variantMobile === ProductListVariant.None ? props.variant : props.variantMobile;

    useEffect(() => {
        window.addEventListener(RecommendationsEventNameResolver.GetEventName(props.model.area), loadProducts);
        recommendationsLoadTimeout.current = setTimeout(() => loadProducts(), RecommendationsTimeoutMs);

        return () => {
            window.removeEventListener(RecommendationsEventNameResolver.GetEventName(props.model.area), loadProducts);
            recommendationsLoadTimeout.current && clearTimeout(recommendationsLoadTimeout.current);
        };
    }, []);

    useEffect(() => {
        setProductListModel({ ...productListModel, products: productList });
    }, [productList]);

    const loadProducts = () => {
        let recommendations: Recommendation[] = [];
        try {
            recommendations = JSON.parse(RecommendationsCache.getRecommendations(props.model.area)!) as Recommendation[];
        } catch (err) {
            ApplicationInsights.TrackTrace(
                SeverityLevel.Error,
                `Failed to parse JSON in loadProducts. Data: ${RecommendationsCache.getRecommendations(props.model.area)} Error: ${err}`
            );
        }

        getProductCards(recommendations);
        recommendationsLoadTimeout.current && clearTimeout(recommendationsLoadTimeout.current);
    };

    const getProductCards = (recs: Recommendation[]) => {
        const fallbackSkus = props.model.fallbackSkus.map((x) => ({ refCode: x, id: undefined } as Recommendation));

        const recommendations = [...(recs || []), ...(fallbackSkus || [])];

        const request = {
            settings: {
                includeOutOfStockItems: props.model.showOutOfStockItems,
                originName: recs?.length > 0 ? props.model.originName : props.model.fallbackOriginName
            },
            recommendations: recommendations
        } as RecommendedProductCardsRequest;

        postFor<ProductCardViewModel[]>(`${props.productsUrl}`, request)
            .then((productList) => {
                setProductList(productList);
            })
            .finally(() => setIsLoading(false));
    };

    const getMobileClassNames = (): (string | false)[] => {
        return [props.model.maxProductsAmountOnMobile % 2 === 1 && 'RecommendedProducts__Inner--MobileRemoveBorderAfterLast'];
    };

    const getDesktopClassNames = (): (string | false)[] => {
        return [
            props.model.maxProductsAmountOnTablet % 3 !== 0 && 'RecommendedProducts__Inner--TabletRemoveBorderAfterLast',
            props.model.maxProductsAmountOnDesktop % 5 !== 0 && 'RecommendedProducts__Inner--DesktopRemoveBorderAfterLast'
        ];
    };

    return (
        <div className="RecommendedProducts">
            <div className={classNames('RecommendedProducts__Inner--Desktop', getDesktopClassNames())}>
                <ProductList
                    model={productListModel}
                    cartItemUrl={props.cartItemUrl}
                    validateItemUrl={props.validateItemUrl}
                    profile={props.profile}
                    rxUrl={props.rxUrl}
                    variant={props.variant}
                    epiFindClickTrackingUrl={props.epiFindClickTrackingUrl}
                    experimentProductClickUrl={props.experimentProductClickUrl}
                    isLoading={isLoading}
                    withPlaceholders
                    skipProductsAmountOnMobile={props.model.skipProductsAmountOnMobile}
                    skipProductsAmountOnTablet={props.model.skipProductsAmountOnTablet}
                    skipProductsAmountOnDesktop={props.model.skipProductsAmountOnDesktop}
                    disableProductQuantityPicker={props.disableProductQuantityPicker}
                />
            </div>
            <div className={classNames('RecommendedProducts__Inner--Mobile', getMobileClassNames())}>
                <ProductList
                    model={productListModel}
                    cartItemUrl={props.cartItemUrl}
                    validateItemUrl={props.validateItemUrl}
                    profile={props.profile}
                    rxUrl={props.rxUrl}
                    variant={variantMobile}
                    epiFindClickTrackingUrl={props.epiFindClickTrackingUrl}
                    experimentProductClickUrl={props.experimentProductClickUrl}
                    isLoading={isLoading}
                    withPlaceholders
                    skipProductsAmountOnMobile={props.model.skipProductsAmountOnMobile}
                    skipProductsAmountOnTablet={props.model.skipProductsAmountOnTablet}
                    skipProductsAmountOnDesktop={props.model.skipProductsAmountOnDesktop}
                    disableProductQuantityPicker={props.disableProductQuantityPicker}
                />
            </div>
        </div>
    );
};
